import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { endpoints } from "../lib/api";

// The backend's limit (src/minutes/personal.py MAX_INSTRUCTION_CHARS).
const MAX_CHARS = 4000;

const PLACEHOLDER = `Who you are, and what you want from each meeting. For example:

I'm the AI lead engineer. Give me the discussion about AI development and AI engineering, and list anything I need to act on as bullet points.`;

/** Starting points people can take and adjust. */
const EXAMPLES = [
  {
    role: "Engineer",
    text: "I'm a backend engineer. Pull out the technical decisions, anything that affects the backend or the API, and the action items for engineers, as bullet points.",
  },
  {
    role: "Team lead",
    text: "I lead the team. Give me a short summary of what was decided, who owns each action item and when it's due, and any risks or blockers raised.",
  },
  {
    role: "Sales",
    text: "I'm in sales. Tell me what the client asked for, their concerns and objections, anything about pricing or timelines, and the follow-ups I should send.",
  },
  {
    role: "HR",
    text: "I work in HR. Note anything about hiring, onboarding, policies, leave or team wellbeing, and any follow-ups for HR.",
  },
  {
    role: "Designer",
    text: "I'm a designer. Give me the feedback on designs and screens, the UI and UX decisions, and what I need to change, as a checklist.",
  },
];

/** Your standing instruction for Personalized Details: written once, used on
 * every meeting where you press "Generate for me". */
export function Personalization() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const saved = useQuery({ queryKey: ["personalization"], queryFn: endpoints.personalization });
  const [text, setText] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);

  // Once, when the saved instruction arrives: the box starts from it.
  useEffect(() => {
    if (saved.data) setText(saved.data.text);
  }, [saved.data]);

  const save = useMutation({
    mutationFn: () => endpoints.savePersonalization(text.trim(), me.data?.csrf_token ?? ""),
    onSuccess: (data) => {
      queryClient.setQueryData(["personalization"], data);
      // Meeting pages say whether their details were written for an older instruction.
      queryClient.invalidateQueries({ queryKey: ["recording"] });
    },
  });

  const savedText = (saved.data?.text ?? "").trim();
  const unchanged = text.trim() === savedText;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  function submit() {
    if (!unchanged && !save.isPending) save.mutate();
  }

  /** Fills an empty box, or adds to what's there, and puts the cursor at the end. */
  function applyExample(example: string) {
    const next = text.trim() ? `${text.trim()}\n\n${example}` : example;
    setText(next.slice(0, MAX_CHARS));
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(box.current.value.length, box.current.value.length);
    });
  }

  let status: React.ReactNode = null;
  if (save.isPending) status = "Saving…";
  else if (save.error) status = <span className="error-text">{(save.error as Error).message}</span>;
  else if (!unchanged) status = <span className="unsaved">Unsaved changes</span>;
  else if (saved.data?.updated_at) status = `Saved ${savedWhen(saved.data.updated_at)}`;

  return (
    <div className="page-body personal-page">
      <header className="meetings-head">
        <div>
          <h1>Personalization</h1>
          <p className="meetings-sub">Tell the Meeting Agent who you are, and each meeting gets read for you.</p>
        </div>
      </header>

      <div className="personal-grid">
        <section className="card personal-editor">
          <div className="cal-head">
            <h2 className="card-title">Your instruction</h2>
            <span className="personal-status">{status}</span>
          </div>
          {saved.error && <div className="error">{(saved.error as Error).message}</div>}
          <textarea
            ref={box}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                submit();
              }
            }}
            placeholder={PLACEHOLDER}
            maxLength={MAX_CHARS}
            disabled={saved.isLoading}
            aria-label="Your instruction"
          />
          <div className="personal-bar">
            <span className="personal-count">
              {words} word{words === 1 ? "" : "s"} · {text.length} / {MAX_CHARS} characters
            </span>
            <span className="personal-actions">
              {!unchanged && (
                <button className="btn-quiet" onClick={() => setText(saved.data?.text ?? "")} disabled={save.isPending}>
                  Discard changes
                </button>
              )}
              <button className="btn-primary" onClick={submit} disabled={unchanged || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </span>
          </div>
          <p className="personal-hint">Ctrl+S saves. Leave it empty to turn Personalized Details off.</p>
        </section>

        <aside className="personal-side">
          <section className="card">
            <h2 className="card-title">How it works</h2>
            <ol className="steps">
              <li>
                <strong>Write it once, here.</strong> Your role, and what you want from meetings: topics, decisions,
                action items, and how to lay it out.
              </li>
              <li>
                <strong>Open any meeting</strong> you were in, and find <em>Personalized details</em>.
              </li>
              <li>
                <strong>Press "Generate for me".</strong> The meeting is read against your instruction, and you get back
                only what matters to you.
              </li>
            </ol>
            <p className="personal-private">Only you see your instruction and what it writes for you.</p>
          </section>

          <section className="card">
            <h2 className="card-title">Examples</h2>
            <p className="personal-hint">Click one to start from it, then make it yours.</p>
            <ul className="examples">
              {EXAMPLES.map((example) => (
                <li key={example.role}>
                  <button className="example" onClick={() => applyExample(example.text)}>
                    <span className="example-role">{example.role}</span>
                    <span className="example-text">{example.text}</span>
                    <span className="example-use">{text.trim() ? "Add" : "Use"}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

/** "12:04" today, otherwise "28 Sep, 12:04". */
function savedWhen(value: string): string {
  const moment = new Date(value);
  const time = moment.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return moment.toDateString() === new Date().toDateString()
    ? time
    : `${moment.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${time}`;
}
